import { useEffect, useMemo, useState } from "react";
import {
  buildImprovementPrompt,
  type Incident,
  type IncidentStatus,
} from "@feedback-hub/contract/browser";

import { ApiError, getIncident, resendAutoReply, updateIncidentStatus } from "../api.js";
import {
  ADMIN_STATUS_LABELS,
  TYPE_LABELS,
  categoryLabel,
  formatDate,
  logDeidentifyOff,
  nextStatusOptions,
  surfaceForIncident,
} from "../format.js";
import { StatusBadge } from "./StatusBadge.js";
import { formatPageTitle, useDocumentTitle } from "../pageTitle.js";

export interface DetailProps {
  appId: string;
  incidentId: string;
  onBack: () => void;
  onUpdated: (incident: Incident) => void;
}

export function Detail({ appId, incidentId, onBack, onUpdated }: DetailProps) {
  const [incident, setIncident] = useState<Incident | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<IncidentStatus>("new");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [deidentify, setDeidentify] = useState(true);
  const [copied, setCopied] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState<string | null>(null);
  useDocumentTitle(
    formatPageTitle(
      incident && incident.appId === appId && incident.incidentId === incidentId ? incident.ref : null,
      "Queue",
    ),
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getIncident(appId, incidentId);
        if (cancelled) return;
        setIncident(res.incident);
        setStatus(res.incident.status);
        setNote(res.incident.resolutionNote ?? "");
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Failed to load incident.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appId, incidentId]);

  const prompt = useMemo(() => {
    if (!incident) return "";
    return buildImprovementPrompt(incident, surfaceForIncident(incident), { deidentify });
  }, [incident, deidentify]);

  async function saveStatus() {
    if (!incident) return;
    setSaving(true);
    setError(null);
    try {
      const res = await updateIncidentStatus(
        incident.appId,
        incident.incidentId,
        status,
        note.trim() || undefined,
      );
      setIncident(res.incident);
      setStatus(res.incident.status);
      setNote(res.incident.resolutionNote ?? "");
      onUpdated(res.incident);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to update status.");
    } finally {
      setSaving(false);
    }
  }

  async function resendConfirmation() {
    if (!incident) return;
    setResending(true);
    setResendResult(null);
    setError(null);
    try {
      const res = await resendAutoReply(incident.appId, incident.incidentId);
      setIncident(res.incident);
      onUpdated(res.incident);
      // The request can succeed while the send still fails, so report the outcome rather
      // than assuming a 200 means the mail went out.
      setResendResult(
        res.emailStatus === "sent"
          ? `Confirmation resent to ${res.incident.contactEmail ?? "the reporter"}.`
          : res.emailStatus === "skipped"
            ? "Skipped: that address is on the suppression list."
            : "Send failed again - check the intake logs.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to resend the confirmation.");
    } finally {
      setResending(false);
    }
  }

  async function copyPrompt() {
    if (!incident) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Clipboard copy failed.");
    }
  }

  function onDeidentifyChange(checked: boolean) {
    if (!checked && incident) logDeidentifyOff(incident);
    setDeidentify(checked);
  }

  if (loading) return <p className="muted">Loading incident…</p>;
  if (!incident) return <p className="error">{error ?? "Incident not found."}</p>;

  const statusOptions = nextStatusOptions(incident.status);
  const ctx = incident.context;

  return (
    <div>
      <button type="button" className="btn ghost" onClick={onBack}>
        ← Back to queue
      </button>

      <div className="panel" style={{ marginTop: "0.75rem" }}>
        <h2 style={{ margin: "0 0 0.35rem", fontSize: "1.2rem" }}>
          <span className="mono">{incident.ref}</span>{" "}
          <StatusBadge status={incident.status} />
          {incident.emailStatus === "failed" && (
            <span className="warn-mark">email failed</span>
          )}
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          {TYPE_LABELS[incident.type]}
          {incident.rating ? ` · ${incident.rating}` : ""}
          {incident.category ? ` · ${categoryLabel(incident.category)}` : ""}
          {" · "}
          {formatDate(incident.createdAt)}
          {" · "}
          {incident.occurrences} occurrence{incident.occurrences === 1 ? "" : "s"}
        </p>

        <div className="toolbar">
          <label>
            Status{" "}
            <select
              className="inline-select"
              value={status}
              disabled={saving}
              onChange={(e) => setStatus(e.target.value as IncidentStatus)}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {ADMIN_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn"
            disabled={saving || status === incident.status}
            onClick={() => void saveStatus()}
          >
            {saving ? "Saving…" : "Update status"}
          </button>
          <label>
            <input
              type="checkbox"
              checked={deidentify}
              onChange={(e) => onDeidentifyChange(e.target.checked)}
            />{" "}
            De-identify
          </label>
          <button type="button" className="btn secondary" onClick={() => void copyPrompt()}>
            {copied ? "Copied!" : "Copy improvement prompt"}
          </button>
          {incident.contactEmail && (
            <button
              type="button"
              className="btn secondary"
              disabled={resending}
              onClick={() => void resendConfirmation()}
              title={`Resend the submission confirmation to ${incident.contactEmail}`}
            >
              {resending ? "Sending…" : "Resend confirmation"}
            </button>
          )}
        </div>

        {resendResult && (
          <p className={resendResult.startsWith("Confirmation resent") ? "muted" : "error"}>
            {resendResult}
          </p>
        )}

        <div className="field" style={{ marginBottom: "0.75rem" }}>
          <label htmlFor="resolution-note">
            Note{status === "wont_fix" ? " (required for won't fix)" : " (optional)"}
          </label>
          <textarea
            id="resolution-note"
            className="note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              status === "wont_fix"
                ? "Required explanation for the reporter"
                : "Optional note for this transition"
            }
          />
        </div>

        {error && <p className="error">{error}</p>}

        <div className="section">
          <h3>What they wrote</h3>
          <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{incident.body}</p>
          {(incident.contactEmail || incident.contactName) && (
            <p className="muted" style={{ marginTop: "0.5rem" }}>
              Contact: {incident.contactName ?? "—"}
              {incident.contactEmail ? ` <${incident.contactEmail}>` : ""}
            </p>
          )}
        </div>

        <div className="section">
          <h3>Context</h3>
          <table className="context-table">
            <tbody>
              {Object.entries({
                surfaceId: ctx.surfaceId,
                route: ctx.route,
                appVersion: ctx.appVersion,
                occurredAt: ctx.occurredAt,
                timezone: ctx.timezone,
                locale: ctx.locale,
                viewport: ctx.viewport,
                deviceClass: ctx.deviceClass,
                browser: ctx.browser,
                os: ctx.os,
                prefersColorScheme: ctx.prefersColorScheme,
                sessionRef: ctx.sessionRef ?? "—",
              }).map(([k, v]) => (
                <tr key={k}>
                  <th>{k}</th>
                  <td className="mono">{String(v)}</td>
                </tr>
              ))}
              {ctx.payload && (
                <tr>
                  <th>payload</th>
                  <td>
                    <pre className="mono" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(ctx.payload, null, 2)}
                    </pre>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="section">
          <h3>Status history</h3>
          <ul className="timeline">
            {incident.statusHistory.map((entry, i) => (
              <li key={`${entry.at}-${entry.status}-${i}`}>
                <StatusBadge status={entry.status} />{" "}
                <span className="muted">{formatDate(entry.at)}</span>
                {entry.note ? ` — ${entry.note}` : ""}
              </li>
            ))}
          </ul>
        </div>

        <div className="section">
          <h3>Improvement prompt preview</h3>
          <pre className="prompt-preview">{prompt}</pre>
        </div>
      </div>
    </div>
  );
}
